import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft, FaEdit, FaPowerOff, FaShieldAlt, FaClock } from "react-icons/fa";
import Handler from "../../../Helpers/Handler";
import { currencyFormatter, formatDateTime, textCapitalize } from "../../../Helpers/Utils";
import { Skeleton, DetailCard, FieldRow } from "../../../Components/Needed";
import Timeline from "../../../Components/Timeline";
import PlanFormModal from "./PlanFormModal";
import { listBackPath } from "../../../Helpers/useListParams";

const ProtectionView = () => {
    const { tpId } = useParams();
    const navigate = useNavigate();

    const [plan, setPlan] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [togglingStatus, setTogglingStatus] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const [detailResponse, logResponse] = await Promise.all([
            Handler({ method: "GET", url: `/admin/booking/protection/${tpId}` }),
            Handler({ method: "GET", url: `/admin/booking/protection/${tpId}/log`, params: { limit: 20 } }),
        ]);
        setPlan(detailResponse?.success ? detailResponse?.data?.data : null);
        setLogs(logResponse?.success ? logResponse?.data?.data || [] : []);
        setLoading(false);
    }, [tpId]);

    useEffect(() => { load(); }, [load]);

    const toggleStatus = async () => {
        if (!plan) return;
        setTogglingStatus(true);
        const response = await Handler({ method: "patch", url: `/admin/booking/protection/${tpId}/status`, params: { status: !plan.status } });
        if (response?.success) {
            toast.success(response?.data?.message || "Status updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update status"));
        }
        setTogglingStatus(false);
    };

    if (loading) {
        return (
            <div className="container-fluid py-3">
                <Skeleton height={20} width="180px" className="mb-4" />
                <div className="bg-white border rounded-4 p-4">
                    <Skeleton height={32} width="40%" className="mb-3" />
                    <Skeleton height={18} width="25%" className="mb-4" />
                    <Skeleton height={120} />
                </div>
            </div>
        );
    }

    if (!plan) {
        return (
            <div className="container-fluid py-4">
                <div className="bg-white border rounded-4 text-center py-5 px-3">
                    <div className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle bg-light" style={{ width: 64, height: 64 }}>
                        <FaShieldAlt className="text-muted" size={24} />
                    </div>
                    <h5 className="fw-semibold mb-2">Trip protection plan not found</h5>
                    <p className="text-muted mb-4">This plan doesn't exist or may have been removed.</p>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(listBackPath("/protection"))}>
                        <FaArrowLeft className="me-2" /> Back to trip protection
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="container-fluid py-3">
                <div className="mb-3">
                    <button type="button" className="btn btn-link text-secondary text-decoration-none p-0 d-inline-flex align-items-center gap-2" onClick={() => navigate(listBackPath("/protection"))}>
                        <FaArrowLeft size={13} />
                        <span>Back to trip protection</span>
                    </button>
                </div>

                <div className="bg-white border rounded-4 shadow-sm mb-4 overflow-hidden">
                    <div className="p-4 p-lg-4">
                        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-4">
                            <div className="d-flex align-items-center gap-3">
                                <div className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0" style={{ width: 56, height: 56, background: "rgba(13, 110, 253, 0.08)" }}>
                                    <FaShieldAlt size={23} className="text-primary" />
                                </div>
                                <div>
                                    <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                                        <h4 className="fw-semibold mb-0 text-capitalize">{plan?.name || ""}</h4>
                                        <span className={`badge rounded-pill ${plan.status ? "text-bg-success" : "text-bg-secondary"}`}>
                                            {plan.status ? "Active" : "Inactive"}
                                        </span>
                                    </div>
                                    <div className="d-flex align-items-center flex-wrap gap-2 text-muted small">
                                        <span>Trip Protection ID:</span>
                                        <span className="font-monospace fw-medium text-dark">{plan.tpId}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex flex-wrap gap-2">
                                <button type="button" className="btn btn-light border d-inline-flex align-items-center gap-2" onClick={toggleStatus} disabled={togglingStatus}>
                                    <FaPowerOff size={13} className={plan.status ? "text-danger" : "text-success"} />
                                    {togglingStatus ? "Updating..." : plan.status ? "Disable" : "Enable"}
                                </button>
                                <button type="button" className="btn btn-primary d-inline-flex align-items-center gap-2" onClick={() => setShowEditModal(true)}>
                                    <FaEdit size={13} /> Edit plan
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="border-top bg-light bg-opacity-50">
                        <div className="row g-0">
                            <div className="col-6 col-md-4">
                                <div className="px-4 py-3 border-end">
                                    <div className="text-muted small mb-1">Percentage</div>
                                    <div className="fw-semibold">{plan.percentage}%</div>
                                </div>
                            </div>
                            <div className="col-6 col-md-4">
                                <div className="px-4 py-3 border-end">
                                    <div className="text-muted small mb-1">Amount</div>
                                    <div className="fw-semibold">{currencyFormatter(plan.amount || 0)}</div>
                                </div>
                            </div>
                            <div className="col-12 col-md-4">
                                <div className="px-4 py-3">
                                    <div className="d-flex align-items-center gap-2 text-muted small mb-1">
                                        <FaClock size={12} /> Last updated
                                    </div>
                                    <div className="fw-medium small">{formatDateTime(plan.updatedAt)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row g-4">
                    <div className="col-12 col-xl-7">
                        <DetailCard title="Description" icon={<FaShieldAlt />}>
                            <div className="bg-light rounded-3 p-3 text-secondary" dangerouslySetInnerHTML={{ __html: plan.description || "-" }} />
                        </DetailCard>

                        <DetailCard title="Plan details">
                            <FieldRow label="Trip Protection ID" value={plan.tpId} />
                            <FieldRow label="Name" value={plan?.name} />
                            <FieldRow label="Percentage" value={`${plan.percentage}%`} />
                            <FieldRow label="Amount" value={currencyFormatter(plan.amount || 0)} />
                            <FieldRow label="Created by" value={plan.createdBy} />
                            <FieldRow label="Updated by" value={plan.updatedBy} />
                            <FieldRow label="Created" value={formatDateTime(plan.createdAt)} />
                            <FieldRow label="Last updated" value={formatDateTime(plan.updatedAt)} />
                        </DetailCard>
                    </div>

                    <div className="col-12 col-xl-5">
                        <Timeline userLog={logs} />
                    </div>
                </div>
            </div>

            {showEditModal ? (
                <PlanFormModal
                    plan={plan}
                    onClose={() => setShowEditModal(false)}
                    onSaved={() => { setShowEditModal(false); load(); }}
                />
            ) : ""}
        </>
    );
};

export default ProtectionView;
